import type { Zone, ZoneLetter, ZoneProgress, ZoneStatus } from "../../api/types";
import { formatNumber, k } from "../../i18n/i18n";

/** État d'une zone libre côté client (docs/14 §2.7, points 5 à 9) : relevé périodique, étapes de la préparation,
 *  pastille durable, compte à rebours d'un refus 429. Fonctions pures, testées (`zoneState.test.ts`). */

/** Relevé de la zone du dossier ouvert pendant sa préparation (docs/14 §2.7.5). */
export const POLL_MS = 1500;
/** Plafond de l'attente entre deux relevés après des échecs réseau successifs. */
export const MAX_BACKOFF_MS = 30_000;

const ACTIVE: readonly ZoneStatus[] = ["queued", "preparing"];

/** Préparation (création, relance) ou extension en file ou en cours. */
export function zoneBusy(zone: Pick<Zone, "status" | "pending_status"> | null | undefined): boolean {
  if (!zone) return false;
  return ACTIVE.includes(zone.status) || (zone.pending_status !== null && ACTIVE.includes(zone.pending_status));
}

/** Extension en file ou en cours d'une zone prête : le terrain reste servi, mais aucun code de transmission n'est
 *  émis avant sa fin (le serveur le refuserait, `ZONE_EXTENDING`). */
export function zoneExtending(zone: Pick<Zone, "status" | "pending_status"> | null | undefined): boolean {
  return zone?.status === "ready" && zone.pending_status !== null && ACTIVE.includes(zone.pending_status);
}

/** Zone utilisable par le dossier (terrain servi), y compris pendant une extension. */
export const zoneReady = (zone: Pick<Zone, "status"> | null | undefined) => zone?.status === "ready";

/** Délai avant le prochain relevé, ou `false` pour l'arrêter :
 *  - zone prête, en échec ou annulée (et sans extension active) : arrêt ;
 *  - zone introuvable (404 : supprimée ailleurs), licence ou poste refusés (401, 403) : arrêt ;
 *  - échecs successifs du relevé (réseau, serveur) : 1,5 s doublé à chaque échec, 30 s au plus ;
 *  - sinon 1,5 s. Tant que l'état n'est pas connu (premier relevé), 1,5 s. */
export function pollDelay(zone: Zone | null | undefined, failures = 0, errorStatus?: number): number | false {
  // Zone supprimée ailleurs (404), licence ou poste refusés (401, 403) : relever ne changerait rien.
  if (errorStatus === 404 || errorStatus === 401 || errorStatus === 403) return false;
  if (zone && !zoneBusy(zone)) return false;
  if (failures > 0) return Math.min(MAX_BACKOFF_MS, POLL_MS * 2 ** failures);
  return POLL_MS;
}

/** Échecs consécutifs du relevé, par zone. TanStack Query remet son propre compteur (`fetchFailureCount`) à zéro au
 *  début de chaque requête : sans `retry`, il ne dépasse jamais 1 et le recul ne se produirait pas. Le relevé tient
 *  donc son compteur : + 1 à chaque échec, remis à zéro au premier succès. */
export function failureCounter() {
  const counts = new Map<string, number>();
  return {
    failed: (id: string) => counts.set(id, (counts.get(id) ?? 0) + 1).get(id)!,
    succeeded: (id: string) => void counts.delete(id),
    count: (id: string) => counts.get(id) ?? 0,
  };
}

/** Passage d'un relevé au suivant qui mérite l'avis « Terrain prêt » et le rafraîchissement du terrain : la zone
 *  devient prête, ou son extension se termine (le terrain servi change). */
export function becameReady(before: Zone | null | undefined, after: Zone | null | undefined): boolean {
  if (!before || !after || before.id !== after.id) return false;
  if (before.status !== "ready" && after.status === "ready") return true;
  return (
    after.status === "ready" &&
    before.pending_status !== null &&
    ACTIVE.includes(before.pending_status) &&
    after.pending_status === null
  );
}

/** Préparation (ou extension) interrompue parce qu'une autre demande de la licence est passée en priorité :
 *  « Reprendre la préparation » est l'action attendue. */
export function zoneSuperseded(zone: Pick<Zone, "superseded"> | null | undefined): boolean {
  return Boolean(zone?.superseded);
}

/** Passage à l'état interrompu (une autre demande de la licence est passée en priorité) : l'avis propose de reprendre. */
export function becameSuperseded(before: Zone | null | undefined, after: Zone | null | undefined): boolean {
  if (!before || !after || before.id !== after.id) return false;
  return !zoneSuperseded(before) && zoneSuperseded(after);
}

/** Passage en échec (création ou extension) : l'avis désigne « Réessayer ». */
export function becameFailed(before: Zone | null | undefined, after: Zone | null | undefined): boolean {
  if (!before || !after || before.id !== after.id) return false;
  return (before.status !== "failed" && after.status === "failed") || (before.pending_status !== "failed" && after.pending_status === "failed");
}

// --- Action attendue d'une zone pas encore utilisable -------------------------------------------------------------

/** Ce qu'attend une zone dont le terrain n'est pas (ou plus) utilisable, pour désigner la bonne action (R12) :
 *  - `progress` : préparation ou extension en file ou en cours, attendre (carte de progression) ;
 *  - `retry` : échec passager (service de données, temps dépassé, service arrêté brutalement à chaque tentative,
 *    `memory`) ou annulation, « Réessayer » ;
 *  - `storage` : échec faute d'espace, « Libérer de l'espace » avant de relancer ;
 *  - `remove` : aucune terre dans l'empreinte (`no_land`), la relance échouerait à l'identique : supprimer la zone et
 *    tracer un nouvel axe sur la terre ferme ;
 *  - null : zone prête, rien n'attend. */
export type ZoneWait = "progress" | "retry" | "storage" | "remove";

export function zoneWait(zone: Zone | null | undefined): ZoneWait | null {
  if (!zone) return null;
  if (zoneBusy(zone)) return "progress";
  const failed = zone.status === "failed" ? zone.progress : zone.pending_status === "failed" ? zone.pending_progress : null;
  if (failed !== null || zone.status === "failed" || zone.pending_status === "failed") {
    const key = failed?.error_key;
    if (key === "no_land") return "remove";
    if (key === "storage") return "storage";
    return "retry";
  }
  return zone.status === "ready" ? null : "retry";
}

// --- Étapes de la préparation ------------------------------------------------------------------------------------

/** Les six étapes du service des zones (`generex_api/zones/builder.STEPS`), libellés de la carte de zone. */
export const ZONE_STEPS: readonly { key: string; label: string }[] = [
  { key: "osm", label: k("Routes et cours d'eau") },
  { key: "landcover", label: k("Bois, eau, mer et localités") },
  { key: "places", label: k("Noms de lieux") },
  { key: "relief", label: k("Relief") },
  { key: "derive", label: k("Assemblage") },
  { key: "write", label: k("Enregistrement") },
];

export type StepState = "done" | "active" | "todo";

/** État de chaque étape d'après la progression : faites avant `step`, celle-ci en cours, les autres à faire ; toutes
 *  faites quand la zone est prête ; aucune en cours tant qu'elle attend dans la file. */
export function stepStates(progress: ZoneProgress | null | undefined, status: ZoneStatus): StepState[] {
  if (status === "ready") return ZONE_STEPS.map(() => "done");
  const step = progress?.step ?? 0;
  return ZONE_STEPS.map((_, i) => {
    const n = i + 1;
    if (n < step) return "done";
    return n === step && status === "preparing" ? "active" : "todo";
  });
}

/** Avancement global (0 à 100, au dixième près) : celui du service, 100 quand la zone est prête. Le service le
 *  rend au dixième (registre K37) : la barre bouge dès la première tuile d'une longue étape. */
export function progressPct(progress: ZoneProgress | null | undefined, status: ZoneStatus): number {
  if (status === "ready") return 100;
  return Math.max(0, Math.min(100, Math.round((progress?.pct ?? 0) * 10) / 10));
}

/** Pourcentage affiché dans la langue de l'interface, au dixième près (« 37,4 », « 41 »). */
export function formatPct(pct: number): string {
  return formatNumber(pct, { maximumFractionDigits: 1 });
}

// --- Durée de la préparation ----------------------------------------------------------------------------------------

/** Part du temps de la préparation écoulée à chaque palier de l'avancement : les étapes n'ont pas la même durée
 *  (première mesure réelle, docs/14 §6.7 : brigade de 3 550 km² en 165 s, dont environ 2 min pour les routes et cours
 *  d'eau, 0 à 40 %, et 40 s pour la couverture du sol et le relief). Sans cette courbe, l'extrapolation linéaire du
 *  pourcentage doublerait le temps restant pendant la première étape. */
const TIME_CURVE: readonly (readonly [number, number])[] = [
  [0, 0],
  [40, 120 / 165],
  [55, 140 / 165],
  [60, 142 / 165],
  [80, 162 / 165],
  [95, 164 / 165],
  [100, 1],
];

/** Même courbe quand les routes et cours d'eau sont lus dans les extraits OSM rangés sur le serveur (`osm_source`
 *  « local », docs/14 §6.11) : l'étape 1 ne dure que quelques secondes, le reste garde ses proportions. */
const LOCAL_TIME_CURVE: readonly (readonly [number, number])[] = [
  [0, 0],
  [40, 1 / 46],
  [55, 21 / 46],
  [60, 23 / 46],
  [80, 43 / 46],
  [95, 45 / 46],
  [100, 1],
];

/** Même courbe pour un terrain de corps d'armée tiré des tuiles vectorielles planétaires (`osm_source` « tiles »,
 *  registre K37) : mesure du 18/10/2026 (corps Pologne–Biélorussie, 46 s sur le poste de développement) : routes et
 *  cours d'eau 15 s, couverture du sol et lieux 8 s, relief 13 s, dérivation 10 s. */
const TILES_TIME_CURVE: readonly (readonly [number, number])[] = [
  [0, 0],
  [40, 0.33],
  [55, 0.39],
  [60, 0.48],
  [80, 0.75],
  [95, 0.97],
  [100, 1],
];

export type OsmSource = "local" | "mixed" | "overpass" | "tiles";

/** Part du temps écoulée (0 à 1) pour un avancement `pct` (0 à 100), interpolée sur `TIME_CURVE` (sur
 *  `LOCAL_TIME_CURVE` pour une préparation lue dans les extraits du serveur). */
export function timeFraction(pct: number, source?: OsmSource | null): number {
  const curve = source === "local" ? LOCAL_TIME_CURVE : source === "tiles" ? TILES_TIME_CURVE : TIME_CURVE;
  const p = Math.max(0, Math.min(100, pct));
  for (let i = 1; i < curve.length; i++) {
    const [p1, f1] = curve[i]!;
    if (p <= p1) {
      const [p0, f0] = curve[i - 1]!;
      return f0 + ((f1 - f0) * (p - p0)) / (p1 - p0);
    }
  }
  return 1;
}

/** L'extrapolation de l'avancement attend 5 % et 20 s de tâche observée. */
export const EXTRAPOLATE_PCT = 5;
export const EXTRAPOLATE_MS = 20_000;
/** Pourcentage inchangé depuis 90 s : serveurs de données lents (avis calme) ; depuis 10 min : rappel que le serveur
 *  arrête de lui-même une tâche sans aucun progrès pendant 15 min (`timeout`, docs/14 §6.7), relançable ensuite. */
export const SLOW_MS = 90_000;
export const STALLED_MS = 600_000;

/** Observation d'une tâche par ce poste. Les horodatages du serveur (`started_at`, `advanced_at`) ne sont jamais
 *  comparés à l'horloge du navigateur, qui peut être décalée de plusieurs minutes : le serveur rend des durées
 *  (`elapsed_s`, `idle_s`, mesurées à son horloge), converties ici en instants de l'horloge du poste au premier
 *  relevé de la tâche ; ensuite, seuls comptent les instants où ce poste voit le pourcentage changer.
 *  - `key` : la tâche observée (zone, création ou extension, début de la tâche) ;
 *  - `origin` : début de la tâche, à l'horloge du poste ;
 *  - `changedAt` : dernier changement du pourcentage vu par ce poste (au premier relevé : le dernier progrès du
 *    serveur, qui peut avancer d'une tuile sans changer le pourcentage — l'attente n'est jamais surestimée).
 *  Le premier relevé peut venir de données gardées (liste des zones relue toutes les 30 s au plus) : `origin` est
 *  affiné à chaque relevé qui porte un temps écoulé, vers le début le plus ancien (`elapsed_s` est une durée, donc
 *  insensible au décalage d'horloge) ; `changedAt` n'est pas reculé (l'avis de lenteur reste du côté calme). */
export interface JobWatch {
  key: string;
  origin: number;
  pct: number;
  changedAt: number;
}

/** Tâche active de la zone (création, relance ou extension) : clé d'observation, état, progression ; null sans tâche
 *  active. Une nouvelle réservation (reprise par le service) change `started_at`, donc la clé. */
export function activeJob(zone: Zone | null | undefined): { key: string; status: ZoneStatus; progress: ZoneProgress | null } | null {
  if (!zone || !zoneBusy(zone)) return null;
  const extending = zoneExtending(zone);
  const status = (extending ? zone.pending_status : zone.status)!;
  const progress = extending ? zone.pending_progress : zone.progress;
  const mark = status === "preparing" ? (progress?.started_at ?? "") : `queued ${progress?.queued_at ?? ""}`;
  return { key: `${zone.id} ${extending ? "extend" : "create"} ${mark}`, status, progress };
}

/** Observation suivante : nouvelle au premier relevé d'une tâche (instants du poste tirés des durées du serveur),
 *  `origin` reculé quand un relevé plus frais montre une tâche commencée plus tôt, `changedAt` remis à `now` quand
 *  le pourcentage change, sinon inchangée (même objet : appel idempotent). */
export function watchJob(
  prev: JobWatch | null | undefined,
  key: string,
  pct: number,
  now: number,
  elapsedS?: number | null,
  idleS?: number | null,
): JobWatch {
  if (!prev || prev.key !== key) {
    const origin = now - Math.max(0, elapsedS ?? 0) * 1000;
    return { key, origin, pct, changedAt: Math.max(origin, now - Math.max(0, idleS ?? 0) * 1000) };
  }
  const origin = elapsedS != null ? Math.min(prev.origin, now - Math.max(0, elapsedS) * 1000) : prev.origin;
  if (pct === prev.pct && origin === prev.origin) return prev;
  return pct === prev.pct ? { ...prev, origin } : { ...prev, origin, pct, changedAt: now };
}

export type DurationHint =
  | { kind: "usual"; ms: number } // durée habituelle du terrain, tant que l'avancement ne permet pas d'extrapoler
  | { kind: "remaining"; ms: number } // temps restant estimé
  | { kind: "estimating" }; // ni durée habituelle (serveur antérieur) ni extrapolation possible

/** Durée à afficher pendant la préparation :
 *  - avant 5 % ou 20 s de tâche : la durée habituelle du terrain (`expected_s` du serveur), dès la première seconde ;
 *  - ensuite, le temps restant : la durée totale estimée mêle le temps écoulé (part faite, `f`) et la durée habituelle
 *    (part restante), `total = écoulé + (1 − f) × habituelle`, et le reste vaut `(1 − f) × total` ; l'estimation part
 *    de la durée habituelle et rejoint l'observation à mesure que la tâche avance, sans saut ; une tâche lente voit
 *    son reste grandir honnêtement. Sans durée habituelle : extrapolation seule, `écoulé × (1 − f) / f`. */
export function durationHint(
  watch: JobWatch,
  pct: number,
  expectedS: number | null | undefined,
  now: number,
  source?: OsmSource | null,
): DurationHint {
  const expected = expectedS && expectedS > 0 ? expectedS * 1000 : null;
  const elapsed = Math.max(0, now - watch.origin);
  if (pct < EXTRAPOLATE_PCT || elapsed < EXTRAPOLATE_MS || pct >= 100)
    return expected !== null ? { kind: "usual", ms: expected } : { kind: "estimating" };
  const f = timeFraction(pct, source);
  if (expected === null) return { kind: "remaining", ms: (elapsed * (1 - f)) / f };
  return { kind: "remaining", ms: (1 - f) * (elapsed + (1 - f) * expected) };
}

/** Lenteur d'une tâche en cours d'après le dernier changement de pourcentage vu par ce poste : `slow` après 90 s,
 *  `stalled` après 10 min, null sinon. */
export function slowness(watch: JobWatch, now: number): "slow" | "stalled" | null {
  const idle = now - watch.changedAt;
  if (idle >= STALLED_MS) return "stalled";
  return idle >= SLOW_MS ? "slow" : null;
}

/** Ce que la tâche attend pendant un pourcentage immobile, pour ne pas accuser à tort les serveurs de données :
 *  `network` pendant les routes et cours d'eau (étape 1, Overpass ; `compute` quand elles sont lues dans les extraits
 *  du serveur, `source` « local ») et le relief (étape 4 jusqu'à 78 %, tuiles d'altitude), `compute` pendant les étapes calculées sur le serveur (couverture du sol, lieux, mer, assemblage,
 *  enregistrement — `builder.build`). */
export function slowCause(progress: ZoneProgress | null | undefined, source?: OsmSource | null): "network" | "compute" {
  const step = progress?.step ?? 0;
  if (step <= 1) return source === "local" ? "compute" : "network";
  return step === 4 && (progress?.pct ?? 0) < 78 ? "network" : "compute";
}

/** Durée arrondie pour l'affichage : minutes (au-delà d'une minute, arrondies au-dessus) ou secondes. */
export function roundedDuration(ms: number): { value: number; unit: "min" | "s" } {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  if (seconds >= 60) return { value: Math.ceil(seconds / 60), unit: "min" };
  return { value: seconds, unit: "s" };
}

// --- Pastille durable (barre latérale, accueil) ---------------------------------------------------------------

export type ZoneChip =
  | { kind: "queued"; position: number | null }
  | { kind: "preparing"; pct: number }
  | { kind: "failed" }
  | { kind: "cancelled" }
  | null;

/** Pastille de la zone du dossier : en file (rang), en préparation (pourcentage), en échec, annulée ; rien quand la
 *  zone est prête sans extension active. Une extension active ou en échec prend la pastille. */
export function zoneChip(zone: Zone | null | undefined): ZoneChip {
  if (!zone) return null;
  const pending = zone.status === "ready" ? zone.pending_status : null;
  const status = pending ?? zone.status;
  const progress = pending ? zone.pending_progress : zone.progress;
  switch (status) {
    case "queued":
      return { kind: "queued", position: zone.queue_position };
    case "preparing":
      return { kind: "preparing", pct: progressPct(progress, "preparing") };
    case "failed":
      return { kind: "failed" };
    case "cancelled":
      return pending ? null : { kind: "cancelled" };
    default:
      return null;
  }
}

// --- Fuseau horaire proposé (K16) ---------------------------------------------------------------------------

/** Dernier dimanche du mois (`month` 1 à 12), à 1 h UTC : bornes de l'heure d'été de l'Union européenne. */
function lastSundayAt1h(year: number, month: number): number {
  const last = new Date(Date.UTC(year, month, 0, 1));
  return last.getTime() - last.getUTCDay() * 86_400_000;
}

/** Heure d'été de l'Union européenne à l'instant `epoch` (`generex_engine.dtg.european_summer_time`). */
export function europeanSummerTime(epoch: number): boolean {
  const year = new Date(epoch).getUTCFullYear();
  return lastSundayAt1h(year, 3) <= epoch && epoch < lastSundayAt1h(year, 10);
}

/** Lettre OTAN proposée par la zone au GDH de début du dossier (`start_dtg`, heure locale sans fuseau, lue en UTC :
 *  l'écart de quelques heures ne compte qu'aux deux nuits de changement d'heure) ; sans GDH, à l'instant présent. */
/** Fuseau horaire tenu pour « pas encore choisi » (K16) : aucun, ou l'heure légale des camps (France : A l'hiver,
 *  B l'été — B est aussi la valeur d'origine d'un dossier). Remplacé par l'heure légale d'une zone libre quand le
 *  dossier y passe ; toute autre lettre (Z…) est un choix délibéré, gardé. Même règle que le serveur
 *  (`routes/zones.py`, `_UNCHOSEN_TIME_ZONES`). */
export function unchosenTimeZone(letter: string | null | undefined): boolean {
  return !letter || letter === "A" || letter === "B";
}

export function proposedTimeZone(
  zone: Pick<Zone, "time_zone" | "time_zone_summer"> | null | undefined,
  startDtg: string | null | undefined,
  now = Date.now(),
): ZoneLetter | null {
  if (!zone?.time_zone) return null;
  const parsed = startDtg ? Date.parse(/(Z|[+-]\d{2}:?\d{2})$/i.test(startDtg) ? startDtg : `${startDtg}Z`) : Number.NaN;
  const at = Number.isFinite(parsed) ? parsed : now;
  return europeanSummerTime(at) ? (zone.time_zone_summer ?? zone.time_zone) : zone.time_zone;
}

// --- Refus 429 : compte à rebours -----------------------------------------------------------------------------

/** Instant jusqu'auquel « Réessayer » reste bloqué après un 429 (`Retry-After`), par zone. */
export function retryUntil(retryAfterMs: number | undefined, now = Date.now()): number | null {
  return retryAfterMs && retryAfterMs > 0 ? now + retryAfterMs : null;
}
